/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { externalCredentialsUpdateGcpIam } from "../funcs/externalCredentialsUpdateGcpIam.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GcpIamCredential } from "../models/components/gcpiamcredential.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpdateGcpIamCredentialRequest,
  UpdateGcpIamCredentialSecurity,
} from "../models/operations/updategcpiamcredential.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UpdateGcpIamCredentialMutationVariables = {
  request: UpdateGcpIamCredentialRequest;
  security?: UpdateGcpIamCredentialSecurity | undefined;
  options?: RequestOptions;
};

export type UpdateGcpIamCredentialMutationData = GcpIamCredential;

export type UpdateGcpIamCredentialMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * updateGcpIamCredential externalCredentials
 *
 * @remarks
 * Replace a GCP IAM external credential's configuration. Requires org:admin.
 */
export function useUpdateGcpIamCredentialMutation(
  options?: MutationHookOptions<
    UpdateGcpIamCredentialMutationData,
    UpdateGcpIamCredentialMutationError,
    UpdateGcpIamCredentialMutationVariables
  >,
): UseMutationResult<
  UpdateGcpIamCredentialMutationData,
  UpdateGcpIamCredentialMutationError,
  UpdateGcpIamCredentialMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUpdateGcpIamCredentialMutation(client, options),
    ...options,
  });
}

export function mutationKeyUpdateGcpIamCredential(): MutationKey {
  return ["@gram/client", "externalCredentials", "updateGcpIam"];
}

export function buildUpdateGcpIamCredentialMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UpdateGcpIamCredentialMutationVariables,
  ) => Promise<UpdateGcpIamCredentialMutationData>;
} {
  return {
    mutationKey: mutationKeyUpdateGcpIamCredential(),
    mutationFn: function updateGcpIamCredentialMutationFn({
      request,
      security,
      options,
    }): Promise<UpdateGcpIamCredentialMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(externalCredentialsUpdateGcpIam(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
